import { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useIntersectionReveal } from "@/hooks/useIntersectionReveal";

type Tone = "light" | "navy";
type Align = "left" | "center";

interface SectionHeaderProps {
  /** Small uppercase label above the headline. */
  eyebrow?: string;
  headline: ReactNode;
  subheadline?: ReactNode;
  /** `navy` renders white copy and a white eyebrow; the chalk line stays red on every ground. */
  tone?: Tone;
  /** Left is the default; the chalk line only makes sense on a shared left edge. */
  align?: Align;
  /** Heading level, default h2. */
  as?: "h1" | "h2" | "h3";
  /** Snap the chalk line in and reveal the copy when scrolled into view. */
  reveal?: boolean;
  className?: string;
  headlineClassName?: string;
  /** Slot rendered to the right of the headline on desktop (e.g. a "View all" button). */
  aside?: ReactNode;
}

/**
 * SectionHeader, eyebrow + chalk line + headline + subheadline.
 *
 * The chalk line is the one signature every Altum piece carries: a 4px rule
 * that bleeds in from the viewport edge and stops inside the text column, and
 * the headline hangs from it. Always brand red.
 */
export function SectionHeader({
  eyebrow,
  headline,
  subheadline,
  tone = "light",
  align = "left",
  as: Heading = "h2",
  reveal = true,
  className,
  headlineClassName,
  aside,
}: SectionHeaderProps) {
  const { ref, isVisible } = useIntersectionReveal<HTMLDivElement>({ threshold: 0.3 });
  const visible = !reveal || isVisible;
  const isNavy = tone === "navy";

  return (
    <div
      ref={ref}
      className={cn(
        "relative",
        isNavy && "on-navy",
        align === "center" && "text-center",
        aside && "md:flex md:items-end md:justify-between md:gap-8",
        className,
      )}
    >
      <div className={cn("max-w-3xl", align === "center" && "mx-auto")}>
        {align === "left" && (
          <div className="mb-5 flex items-center gap-4">
            <span
              aria-hidden="true"
              className={cn("chalk-line chalk-line--snap", visible && "is-visible")}
            />
            {eyebrow && <span className="section-eyebrow !mb-0">{eyebrow}</span>}
          </div>
        )}
        {align === "center" && eyebrow && <span className="section-eyebrow">{eyebrow}</span>}
        <Heading
          className={cn(
            "reveal-up font-heading text-4xl font-bold uppercase leading-[1.05] tracking-tight md:text-5xl",
            isNavy ? "text-white" : "text-navy",
            visible && "is-visible",
            headlineClassName,
          )}
        >
          {headline}
        </Heading>
        {subheadline && (
          <p
            className={cn(
              "reveal-up mt-5 max-w-2xl text-lg leading-relaxed [transition-delay:150ms]",
              isNavy ? "text-on-navy" : "text-muted-foreground",
              align === "center" && "mx-auto",
              visible && "is-visible",
            )}
          >
            {subheadline}
          </p>
        )}
      </div>
      {aside && <div className="mt-6 md:mt-0 md:shrink-0">{aside}</div>}
    </div>
  );
}

export default SectionHeader;
